import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { regionInfo } from '../config/hotelTexts';
import { getHotelText } from '../config/hotelTexts';
import { useLanguage } from '../context/LanguageContext';

// Placeholder images - à remplacer par les vraies photos plus tard
const regionImages = {
  'mont-saint-michel': '/images/mont-customerly-DKKkMfzQE3o-unsplash.jpg',
  'plages-debarquement': '/images/beach-derick-mckinney-Lx9DEJ1jLuw-unsplash.jpg',
  'atelier-cuivre': '/images/copper-matt-seymour-UbsDLjqZ9RM-unsplash.jpg',
  'fonderie': '/images/cloche-luis-felipe-lins-ICccs4u7-yM-unsplash.jpg',
};

const RegionSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const RegionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  position: relative;
  padding-bottom: ${({ theme }) => theme.spacing.md};
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const RegionDescription = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
  font-size: 1.1rem;
`;

const AttractionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
`;

const AttractionCard = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.medium}, box-shadow ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const AttractionImage = styled.div`
  height: 200px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
`;

const AttractionContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const AttractionTitle = styled.h3`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: 1.3rem;
`;

const AttractionDescription = styled.p`
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const AttractionDistance = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  
  svg {
    margin-right: ${({ theme }) => theme.spacing.xs};
  }
`;

const ViewAllButton = styled(Link)`
  display: block;
  width: fit-content;
  margin: ${({ theme }) => theme.spacing.xl} auto 0;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  text-decoration: none;
  transition: all ${({ theme }) => theme.transitions.medium};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const RegionPreview = () => {
  const { t, language } = useLanguage();
  
  // Utiliser les textes multilingues
  const regionTitle = getHotelText(language, 'region.title');
  const regionDescription = getHotelText(language, 'region.description');
  const attractions = getHotelText(language, 'region.attractions') || regionInfo.attractions;
  
  return (
    <RegionSection>
      <RegionContainer>
        <SectionTitle>{regionTitle}</SectionTitle>
        <RegionDescription>{regionDescription}</RegionDescription>
        
        <AttractionsGrid>
          {attractions.slice(0, 3).map((attraction, index) => (
            <AttractionCard key={index}>
              <AttractionImage 
                image={regionImages[attraction.image.split('.')[0]] || regionImages['mont-saint-michel']} 
              />
              <AttractionContent>
                <AttractionTitle>{attraction.name}</AttractionTitle>
                <AttractionDescription>{attraction.shortDescription}</AttractionDescription>
                <AttractionDistance>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                  </svg>
                  {t('region.distanceFrom', { distance: attraction.distance })}
                </AttractionDistance>
              </AttractionContent>
            </AttractionCard>
          ))}
        </AttractionsGrid>
        
        <ViewAllButton to={`/${language}/alentours`}>{t('region.discoverRegion')}</ViewAllButton>
      </RegionContainer>
    </RegionSection>
  );
};

export default RegionPreview;
